/* eslint-disable @typescript-eslint/member-ordering */
import {Injectable} from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot as NgfQuerySnapshot,
  startAfter,
  where,
} from '@angular/fire/firestore';
import {forkJoin, from, Observable, of} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {encodeStrict} from '../../../shared/shared-utils/firestore/firestore.utils';

import {
  ApiLogObj2,
  sItemIntToKey,
  sItemKeyToInt,
  StockItemUpdateObj,
} from '../../../shared-utilities/models-old/datastructures';
import {
  collectStockUpdateFieldKeys,
  mergeTagChangesIntoStockViewChanges,
} from '../../notifications/utils/notification-stock-update-view-changes.utils';
import {
  formatStockHistoryCreatedDate,
  formatStockHistoryValue,
  IStockHistoryEvent,
  IStockHistoryItemOrigin,
  IStockHistoryPageResult,
  IStockHistorySelectedItemSnapshot,
  STOCK_HISTORY_MAX_SELECTED_ITEMS,
} from '../models/stock-history-interfaces';
import {PropertyToTitle} from '../../../shared/shared-utils/conversions/properties';
import {IColleagues} from '../../user-access/models';

/** Backend api_events success values (legacy stock history). */
const STOCK_HISTORY_SUCCESS_STATUSES: string[] = ['SUCCESS', 'COMPLETE'];

interface IFetchSnapshotResult {
  hasMoreInFirestore: boolean;
  snapshot: NgfQuerySnapshot<ApiLogObj2>;
  /** True when Firestore index is missing — docs are sorted locally and pagination is disabled. */
  sortDocsClientSide: boolean;
}

@Injectable({providedIn: 'root'})
export class StockHistoryService {
  private readonly pageSize = 20;
  /** Max api_events docs loaded when composite index is not available yet. */
  private readonly unorderedFetchLimit = 150;

  constructor(private readonly firestore: Firestore) {
  }

  getStockHistoryPage(
    storeId: string,
    itemCodes: string[],
    field: string,
    colleagues: IColleagues,
    lastDocId?: string | null,
  ): Observable<IStockHistoryPageResult> {
    const normalisedCodes: string[] = this.normaliseItemCodes(itemCodes);
    if (normalisedCodes.length === 0) {
      return of({events: [], lastDocumentId: null, hasMore: true});
    }
    return this.fetchRecursively(storeId, normalisedCodes, field, colleagues, lastDocId ?? null, []);
  }

  /**
   * Resolves stock creation date and the user who created each item (from new-stock api_events).
   */
  getItemCreationMeta(
    storeId: string,
    selectedItems: IStockHistorySelectedItemSnapshot[],
    colleagues: IColleagues,
  ): Observable<IStockHistoryItemOrigin[]> {
    const snapshots: IStockHistorySelectedItemSnapshot[] = selectedItems ?? [];
    if (!storeId || snapshots.length === 0) {
      return of([]);
    }
    return forkJoin(
      snapshots.map((snapshot: IStockHistorySelectedItemSnapshot) =>
        from(this.loadItemOrigin(storeId, snapshot, colleagues)),
      ),
    );
  }

  // --- internals ---

  private normaliseItemCodes(itemCodes: string[]): string[] {
    const uniqueCodes: string[] = [];
    const seen: Set<string> = new Set<string>();
    for (const code of itemCodes ?? []) {
      const trimmed: string = String(code ?? '').trim();
      if (!trimmed || seen.has(trimmed)) {
        continue;
      }
      seen.add(trimmed);
      uniqueCodes.push(trimmed);
      if (uniqueCodes.length >= STOCK_HISTORY_MAX_SELECTED_ITEMS) {
        break;
      }
    }
    uniqueCodes.sort((left: string, right: string): number =>
      left.localeCompare(right, undefined, {numeric: true, sensitivity: 'base'}),
    );
    return uniqueCodes;
  }

  private fetchRecursively(
    storeId: string,
    itemCodes: string[],
    field: string,
    colleagues: IColleagues,
    lastDocId: string | null,
    accumulated: IStockHistoryEvent[],
  ): Observable<IStockHistoryPageResult> {
    return from(this.fetchSnapshot(storeId, itemCodes, lastDocId)).pipe(
      switchMap(({snapshot, hasMoreInFirestore, sortDocsClientSide}: IFetchSnapshotResult) => {
        const docs: QueryDocumentSnapshot<ApiLogObj2>[] = sortDocsClientSide
          ? [...snapshot.docs].sort(
            (left: QueryDocumentSnapshot<ApiLogObj2>, right: QueryDocumentSnapshot<ApiLogObj2>): number =>
              this.getApiEventSortTime(right.data()) - this.getApiEventSortTime(left.data()),
          )
          : snapshot.docs;
        const allRaw = docs.map((d: QueryDocumentSnapshot<ApiLogObj2>) => ({...d.data(), id: d.id} as ApiLogObj2));
        const processedEvents: IStockHistoryEvent[] = [];

        allRaw.forEach((evt: ApiLogObj2) => {
          if (evt.type && evt.type !== 'STOCK_UPDATE') {
            return;
          }
          if (evt.status && !STOCK_HISTORY_SUCCESS_STATUSES.includes(evt.status)) {
            return;
          }
          if (!evt.data) {
            return;
          }

          itemCodes.forEach((itemCode: string) => {
            const itemData: StockItemUpdateObj | undefined = this.resolveItemUpdate(evt.data, itemCode);
            if (!itemData) {
              return;
            }

            const mergedChanges: Record<string, StockItemUpdateObj> = mergeTagChangesIntoStockViewChanges(
              {[itemCode]: itemData},
              evt.taggedItems,
            );
            const mergedItemData: StockItemUpdateObj = mergedChanges[itemCode] ?? itemData;
            const fieldKeys: Set<string> = collectStockUpdateFieldKeys(mergedItemData);

            fieldKeys.forEach((propertyIndex: string) => {
              const propertyKey: string = String(
                sItemIntToKey[propertyIndex as keyof typeof sItemIntToKey] ?? propertyIndex,
              );
              if (field !== 'All' && propertyKey !== field) {
                return;
              }

              const originalValue = mergedItemData.o?.[propertyIndex];
              const newValue = mergedItemData.n?.[propertyIndex];

              if (originalValue !== undefined || newValue !== undefined) {
                const propertyName = PropertyToTitle[propertyKey] || propertyKey;
                const eventId = `${evt.id}_${itemCode}_${propertyKey}`;
                const originalText = formatStockHistoryValue(originalValue);
                const newText = formatStockHistoryValue(newValue);

                processedEvents.push({
                  ...evt,
                  id: eventId,
                  stockCode: itemCode,
                  fieldName: propertyName,
                  originalValue: originalText,
                  newValue: newText,
                });
              }
            });
          });
        });

        const uniqueFiltered = processedEvents.filter(
          (evt: IStockHistoryEvent) => !accumulated.some(prev => prev.id === evt.id),
        );

        const combined = [...accumulated, ...uniqueFiltered];
        const lastRawId: string | null = snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1].id
          : lastDocId;

        if (
          !sortDocsClientSide &&
          combined.length < this.pageSize &&
          hasMoreInFirestore &&
          snapshot.docs.length > 0
        ) {
          return this.fetchRecursively(storeId, itemCodes, field, colleagues, lastRawId, combined);
        }

        if (sortDocsClientSide) {
          return of<IStockHistoryPageResult>({
            events: combined.slice(0, this.unorderedFetchLimit),
            lastDocumentId: null,
            hasMore: false,
          });
        }

        const page = combined.slice(0, this.pageSize);
        const hasMore = hasMoreInFirestore || combined.length > this.pageSize;

        return of<IStockHistoryPageResult>({
          events: page,
          lastDocumentId: lastRawId,
          hasMore,
        });
      }),
      catchError(err => {
        console.error('Error fetching stock history:', err);
        return of<IStockHistoryPageResult>({events: [], lastDocumentId: null, hasMore: false});
      }),
    );
  }

  private async loadItemOrigin(
    storeId: string,
    snapshot: IStockHistorySelectedItemSnapshot,
    colleagues: IColleagues,
  ): Promise<IStockHistoryItemOrigin> {
    const code: string = String(snapshot.code ?? '').trim();
    let createdRaw: unknown = snapshot.created;
    if (createdRaw === undefined || createdRaw === null || createdRaw === '') {
      createdRaw = await this.readStockCreatedDateFromFirestore(storeId, code);
    }

    const createdByUserId: string = await this.findNewStockItemCreatorUserId(storeId, code);
    const createdByDisplayName: string = createdByUserId
      ? (colleagues[createdByUserId]?.userData?.userName ?? createdByUserId)
      : '';

    return {
      code,
      createdDisplay: formatStockHistoryCreatedDate(createdRaw),
      createdByDisplayName,
      createdByUserId,
    };
  }

  private async readStockCreatedDateFromFirestore(storeId: string, stockCode: string): Promise<unknown> {
    const stockPath: string = createSharedPaths().setStoreId(storeId).stock();
    const snap = await getDoc(doc(this.firestore, `${stockPath}/${encodeStrict(stockCode)}`));
    if (!snap.exists()) {
      return undefined;
    }
    const raw: Record<string, unknown> = snap.data() as Record<string, unknown>;
    return raw[sItemKeyToInt.created] ?? raw.created;
  }

  private async findNewStockItemCreatorUserId(storeId: string, stockCode: string): Promise<string> {
    const path = `/operational/stores_data/${storeId}/data/api_events`;
    const collectionRef = collection(this.firestore, path);

    try {
      const snapshot = await getDocs(
        query(
          collectionRef,
          where('editedItems', 'array-contains', stockCode),
          where('newStockItem', '==', true),
          orderBy('executedDate', 'asc'),
          limit(1),
        ),
      );
      if (snapshot.empty) {
        return '';
      }
      const event: ApiLogObj2 = snapshot.docs[0].data() as ApiLogObj2;
      return event.userId ?? '';
    } catch (error: unknown) {
      if (!this.isMissingIndexError(error)) {
        console.warn('[Stock History] Unable to load new-stock creator for', stockCode, error);
        return '';
      }
      console.warn(
        '[Stock History] Creator composite index missing; scanning api_events for new-stock record. '
        + 'Deploy firestore.indexes.json or create the index from the Firebase console link in the error.',
        error,
      );
      return this.findNewStockItemCreatorUserIdFallback(collectionRef, stockCode);
    }
  }

  /**
   * When editedItems + newStockItem index is not deployed, array-contains alone still works;
   * pick the earliest new-stock api_event for this code in the fetched batch.
   */
  private async findNewStockItemCreatorUserIdFallback(
    collectionRef: ReturnType<typeof collection>,
    stockCode: string,
  ): Promise<string> {
    try {
      const snapshot = await getDocs(
        query(collectionRef, where('editedItems', 'array-contains', stockCode), limit(this.unorderedFetchLimit)),
      );
      const sortedDocs: QueryDocumentSnapshot[] = [...snapshot.docs].sort(
        (a: QueryDocumentSnapshot, b: QueryDocumentSnapshot): number =>
          this.getApiEventSortTime(a.data() as ApiLogObj2) - this.getApiEventSortTime(b.data() as ApiLogObj2),
      );
      for (const docSnap of sortedDocs) {
        const event: ApiLogObj2 = docSnap.data() as ApiLogObj2;
        if (event.newStockItem === true) {
          return event.userId ?? '';
        }
      }
      return '';
    } catch (fallbackError: unknown) {
      console.warn('[Stock History] Fallback creator lookup failed for', stockCode, fallbackError);
      return '';
    }
  }

  private resolveItemUpdate(
    data: Record<string, StockItemUpdateObj>,
    itemCode: string,
  ): StockItemUpdateObj | undefined {
    if (data[itemCode]) {
      return data[itemCode];
    }
    const normalizedTarget: string = String(itemCode).trim();
    const matchedKey: string | undefined = Object.keys(data).find(
      (key: string) => String(key).trim() === normalizedTarget,
    );
    return matchedKey ? data[matchedKey] : undefined;
  }

  private async fetchSnapshot(
    storeId: string,
    itemCodes: string[],
    lastDocId: string | null,
  ): Promise<IFetchSnapshotResult> {
    try {
      const snapshot = await this.runIndexedStockHistoryQuery(storeId, itemCodes, lastDocId);
      return {
        snapshot,
        hasMoreInFirestore: snapshot.size === this.pageSize,
        sortDocsClientSide: false,
      };
    } catch (error: unknown) {
      if (!this.isMissingIndexError(error)) {
        throw error;
      }
      if (lastDocId) {
        throw error;
      }
      console.warn(
        '[Stock History] Composite index missing; loading a recent batch without server-side sort. '
        + 'Deploy firestore.indexes.json or create the index from the Firebase console link in the error.',
        error,
      );
      const snapshot = await this.runUnorderedStockHistoryQuery(storeId, itemCodes);
      return {
        snapshot,
        hasMoreInFirestore: false,
        sortDocsClientSide: true,
      };
    }
  }

  /**
   * Legacy index shape used by stock history (editedItems + SUCCESS + executedDate).
   */
  private async runIndexedStockHistoryQuery(
    storeId: string,
    itemCodes: string[],
    lastDocId: string | null,
  ): Promise<NgfQuerySnapshot<ApiLogObj2>> {
    const path = `/operational/stores_data/${storeId}/data/api_events`;
    const collectionRef = collection(this.firestore, path);

    let q = query(
      collectionRef,
      where('editedItems', 'array-contains-any', itemCodes),
      where('status', '==', 'SUCCESS'),
      orderBy('executedDate', 'desc'),
      limit(this.pageSize),
    );

    if (lastDocId) {
      const lastSnap = await getDoc(doc(this.firestore, path, lastDocId));
      if (lastSnap.exists()) {
        q = query(q, startAfter(lastSnap));
      }
    }

    return getDocs(q) as Promise<NgfQuerySnapshot<ApiLogObj2>>;
  }

  /** No composite index: array-contains-any only; filter/sort in the client. */
  private async runUnorderedStockHistoryQuery(
    storeId: string,
    itemCodes: string[],
  ): Promise<NgfQuerySnapshot<ApiLogObj2>> {
    const path = `/operational/stores_data/${storeId}/data/api_events`;
    return getDocs(
      query(
        collection(this.firestore, path),
        where('editedItems', 'array-contains-any', itemCodes),
        limit(this.unorderedFetchLimit),
      ),
    ) as Promise<NgfQuerySnapshot<ApiLogObj2>>;
  }

  private isMissingIndexError(error: unknown): boolean {
    const firebaseError = error as { code?: string; message?: string };
    return firebaseError.code === 'failed-precondition'
      || (firebaseError.message?.includes('requires an index') ?? false);
  }

  private getApiEventSortTime(evt: ApiLogObj2): number {
    const value: unknown = evt.executedDate ?? evt.creationDate;
    if (!value) {
      return 0;
    }
    if (value instanceof Date) {
      return value.getTime();
    }
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
      return value.toDate().getTime();
    }
    if (typeof value === 'object' && value !== null && 'seconds' in value && typeof value.seconds === 'number') {
      return value.seconds * 1000;
    }
    return 0;
  }
}
